// Shared motion preference check.
//
// This predicate was copy-pasted into five files (useIdleBored,
// usePupilTracking, ConfettiBurst, useKpiCountUp, OttoAlive) before this
// module existed. Identical logic in five places is the kind of thing that
// drifts — one copy gets a guard the others don't. New code imports from
// here; the existing five can migrate as those files are next touched.
//
// Guards both window and matchMedia because this runs in jsdom under test,
// where matchMedia is not implemented. Absent support = no stated
// preference = motion allowed.
export const prefersReducedMotion = () =>
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Scroll behaviour honouring the preference — 'auto' jumps, 'smooth'
// animates. Use anywhere scrollIntoView is called so no caller has to
// remember the check.
export const scrollBehavior = () => (prefersReducedMotion() ? 'auto' : 'smooth');

// ---------------------------------------------------------------------------
// Otto motion, as a per-user preference
// ---------------------------------------------------------------------------
//
// Separate from prefersReducedMotion above, and deliberately so. The OS setting
// is a statement about ALL motion on the machine; this one is a statement about
// the mascot specifically. Someone can want the owl to sit still without
// wanting every transition in the product to stop, and someone with reduced
// motion set system-wide should get a still Otto without having to find this
// toggle at all. So the two are ORed, never conflated:
//
//   ottoMotionSuppressed() === OS says reduce  OR  this user turned Otto off
//
// The preference is carried on the root element rather than through React
// context or a prop. Otto renders from roughly twenty places — loading curtains,
// empty states, the roamer, the award throw, the briefing bar — and most of his
// motion is CSS keyframes, which a context cannot reach. One attribute on <html>
// is readable by both the stylesheet and the JS schedulers, applies the instant
// it changes, and has nothing to fall out of step with.
//
// "off" is the only meaningful value; absence means on. Written that way so the
// default state adds no attribute at all and the DOM stays clean for the large
// majority who never touch this.
export const OTTO_MOTION_ATTR = 'data-otto-motion';

const root = () => (typeof document !== 'undefined' ? document.documentElement : null);

// enabled === true removes the attribute; false stamps it "off". Null/undefined
// mean "not loaded yet" and are treated as enabled, so Otto does not visibly
// freeze for a moment on every page load while the user row is in flight.
export function applyOttoMotionPreference(enabled) {
  const el = root();
  if (!el) return;
  if (enabled === false) el.setAttribute(OTTO_MOTION_ATTR, 'off');
  else el.removeAttribute(OTTO_MOTION_ATTR);
}

export const ottoMotionDisabled = () => {
  const el = root();
  return !!el && el.getAttribute(OTTO_MOTION_ATTR) === 'off';
};

// The one predicate every Otto scheduler should ask. Both halves matter: the
// CSS blocks below cover keyframes, but a JS timer that keeps firing would
// still reposition him, and a roamer that keeps scheduling would still burn
// work for motion nobody can see.
export const ottoMotionSuppressed = () => prefersReducedMotion() || ottoMotionDisabled();
